import { useEffect, useState } from 'react'

import { quantApi } from '@/app/lib/quant/quantApi'
import type { SourcesView } from '@/app/lib/quant/formatInstrument'
import type { DataCoverage, InstrumentCoverage, InstrumentSources } from '@/app/lib/quant/types'
import { registerSessionReset } from '@/app/lib/sessionReset'

export type QuantDataState =
  | { status: 'loading' }
  | { status: 'loaded'; data: DataCoverage }
  | { status: 'error' }

/**
 * The stored-data coverage (`GET /quant/data`), read once per mount. A failure
 * is silent by design: the date bounds and the "data through" label simply do
 * not appear. No polling and no cache.
 */
export function useQuantData(): QuantDataState {
  const [state, setState] = useState<QuantDataState>({ status: 'loading' })

  useEffect(() => {
    let cancelled = false
    quantApi
      .getData()
      .then((data) => {
        if (!cancelled) setState({ status: 'loaded', data })
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [])

  return state
}

/** The coverage row for an instrument, or `null` (no data yet, empty list, unknown instrument). */
export function coverageRowFor(
  data: DataCoverage | null,
  instrument: string,
): InstrumentCoverage | null {
  if (data === null) return null
  return data.instruments.find((row) => row.instrument === instrument) ?? null
}

let cachedSources: InstrumentSources | null = null
let inflightSources: Promise<InstrumentSources> | null = null

registerSessionReset(() => {
  cachedSources = null
  inflightSources = null
})

function loadSources(): Promise<InstrumentSources> {
  if (cachedSources !== null) return Promise.resolve(cachedSources)
  if (inflightSources === null) {
    const request = quantApi
      .getInstrumentSources()
      .then((sources) => {
        cachedSources = sources
        return sources
      })
      .finally(() => {
        if (inflightSources === request) inflightSources = null
      })
    inflightSources = request
  }
  return inflightSources
}

/**
 * The instrument data-source labels (`GET /quant/instrument-sources`, spec 073),
 * read once per session and cached; a failure is not cached, so the next mount
 * tries again. `enabled=false` makes no request (a strategy with no instrument).
 */
export function useInstrumentSources(enabled = true): SourcesView | null {
  const [state, setState] = useState<SourcesView>(
    cachedSources !== null ? { status: 'loaded', sources: cachedSources } : { status: 'loading' },
  )
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    loadSources()
      .then((sources) => {
        if (!cancelled) setState({ status: 'loaded', sources })
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' })
      })
    return () => {
      cancelled = true
    }
  }, [enabled])
  return enabled ? state : null
}
